import React, { useEffect, useState } from "react";
import { tt } from "../../utils/i18n/i18n";
import { Modal } from "react-bootstrap";
import { useManagedAccountsSelector } from "../../redux/actions/user";
import AddressEditor from "../../components/AddressEditor/AddressEditor";
import { type POSADeliveryWithBill } from "./api";
import BillStatus from "@tootsweet/model/entities/enum/BillStatus";
import CheckoutFormOuter from "../../components/CheckoutForm/CheckoutFormOuter";
import { POSA } from "../../utils/routes/routes";
import { buildRedirectURL } from "../../utils/helpers/buildRedirectUrl";
import KC_Button, {
  KC_ButtonType
} from "../../designsystem/KC_Button/KC_Button";
import type ManagedAccount from "@tootsweet/model/entities/ManagedAccount";

export interface ArtworkModalData {
  show: boolean;
  fileId?: string;
  doNotValidate: boolean;
  validate: boolean;
  delivery?: POSADeliveryWithBill;
}

interface Props {
  setModalData: (data: ArtworkModalData) => void;
  modalData: ArtworkModalData;
  hideModal: () => void;
  validate: () => void;
  doNotValidate: (comment: string) => void;
  setIsLoading: (loading: boolean) => void;
  afterPaymentSuccessful: (paymentIntentId?: string) => void;
}

const STEP_1_DELIVERY_ADDRESS = 0;
const STEP_2_PAY = 1;
const STEP_3_VALIDATE_ARTWORK = 2;
const STEP_4_FINISHED = 3;

export default function ArtworkModal(props: Props) {
  const [comment, setComment] = useState("");
  const [deliveryOwnerAccount, setDeliveryOwnerAccount] =
    useState<ManagedAccount | null>(null);
  const accounts = useManagedAccountsSelector();
  const [step, setStep] = useState(STEP_1_DELIVERY_ADDRESS);

  const submitModalForm = (e: React.SyntheticEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    props.doNotValidate(comment);
  };

  useEffect(() => {
    if (props.modalData.delivery == null) {
      return;
    }
    const deliveryOwnerAccount = accounts.find(
      (ac) => ac.trackingID === props.modalData.delivery?.businessUserId
    );
    if (deliveryOwnerAccount != null) {
      setDeliveryOwnerAccount(deliveryOwnerAccount);
    }

    if (deliveryOwnerAccount?.businessUser?.shippingAddress == null) {
      setStep(STEP_1_DELIVERY_ADDRESS);
    } else if (props.modalData?.delivery?.bill?.status !== BillStatus.Paid) {
      setStep(STEP_2_PAY);
    } else if (deliveryOwnerAccount?.businessUser?.shippingAddress) {
      setStep(STEP_3_VALIDATE_ARTWORK);
    } else {
      setStep(STEP_4_FINISHED);
    }
  }, [props.modalData, accounts]);

  const afterPayment = () => {
    props.afterPaymentSuccessful();
    setStep(STEP_3_VALIDATE_ARTWORK);
  };

  const placeName =
    accounts?.find(
      (acc) => acc.trackingID === props.modalData.delivery?.businessUserId
    )?.placeName || "???";

  // todo : MISSED THIS DESIGN
  return (
    <Modal
      id={"artwork-modal-container"}
      show={props.modalData.show}
      onHide={() => {
        window.location.href = buildRedirectURL(POSA, false);
      }}
      size={step === STEP_3_VALIDATE_ARTWORK ? "xl" : "lg"}
    >
      <Modal.Header>
        <Modal.Title>{tt("posa_validate_artwork_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column">
        {step === STEP_1_DELIVERY_ADDRESS && (
          <>
            <p>
              {tt("must_enter_shipping_address", {
                placeName
              })}
            </p>
            <AddressEditor
              // @ts-expect-error
              trackingID={deliveryOwnerAccount?.trackingID}
              address={deliveryOwnerAccount?.businessUser?.shippingAddress}
              setIsLoading={props.setIsLoading}
            />
          </>
        )}
        {step === STEP_2_PAY &&
          deliveryOwnerAccount?.trackingID &&
          props.modalData.delivery?.bill != null && (
            <>
              <p>{tt("posa_must_pay_before_validate")}</p>
              <CheckoutFormOuter
                amount={props.modalData.delivery.bill.amount}
                posaDeliveryIds={[props.modalData.delivery.id]}
                businessUserId={deliveryOwnerAccount.email}
                onFinished={afterPayment}
                isSaasSubscriptionForm={false}
              />
            </>
          )}
        {step === STEP_3_VALIDATE_ARTWORK && (
          <>
            {!props.modalData.fileId && <p>{tt("api_error")}</p>}
            {!!props.modalData.fileId && (
              <>
                {!props.modalData.doNotValidate && (
                  <>
                    <p>
                      {tt("posa_validate_explanation", {
                        validate_button_text: tt("validate"),
                        do_not_validate_button_text: tt("do_not_validate")
                      })}
                    </p>
                    <div
                      id="posa-validate-action-wrapper"
                      style={{
                        width: "50%",
                        display: "flex",
                        gap: "0.5em",
                        padding: "0.5em"
                      }}
                    >
                      <KC_Button
                        id={"posa-validate-btn"}
                        isPrimary
                        text={tt("validate")}
                        className="d-flex "
                        onClick={() => {
                          setStep(STEP_4_FINISHED);
                          props.validate();
                        }}
                      />
                      <KC_Button
                        id={"posa-do-not-validate-btn"}
                        isPrimary
                        text={tt("do_not_validate")}
                        className="d-flex "
                        onClick={() => {
                          props.setModalData({
                            ...props.modalData,
                            doNotValidate: true
                          });
                        }}
                      />
                    </div>
                  </>
                )}
                {props.modalData.doNotValidate && (
                  <form
                    className="d-flex flex-column align-items-start mb-3"
                    onSubmit={submitModalForm}
                  >
                    <textarea
                      id={"posa-send-comment-textarea"}
                      className="form-control mb-3"
                      rows={3}
                      placeholder={tt("comments")}
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                      }}
                      required={true}
                    />
                    <KC_Button
                      id={"posa-send-comment-btn"}
                      isPrimary
                      text={tt("send_comment")}
                      type={KC_ButtonType.Submit}
                      className="d-flex "
                      disabled={!comment}
                    />
                  </form>
                )}
                <iframe
                  src={`https://drive.google.com/file/d/${props.modalData.fileId}/preview`}
                  width="100%"
                  height={500}
                  allow="autoplay"
                />
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
